import { Badge } from "@/components/ui/badge";
import { AvailabilityStatus } from "@/hooks/useProjectPeople";
import { cn } from "@/lib/utils";
import {
  HelpCircle,
  Send,
  XCircle,
  CheckCircle,
  UserCheck,
} from "lucide-react";

const statusConfig: Record<
  AvailabilityStatus,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    className: string;
    icon: React.ElementType;
  }
> = {
  unknown: {
    label: "To request",
    variant: "secondary",
    className: "bg-muted text-muted-foreground",
    icon: HelpCircle,
  },
  requested: {
    label: "Requested",
    variant: "outline",
    className:
      "border-amber-500 text-amber-600 bg-amber-50 dark:bg-amber-950/20",
    icon: Send,
  },
  not_available: {
    label: "Unavailable",
    variant: "destructive",
    className: "bg-destructive/10 text-destructive border-destructive/30",
    icon: XCircle,
  },
  available: {
    label: "Available",
    variant: "outline",
    className:
      "border-emerald-500 text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20",
    icon: CheckCircle,
  },
  booked: {
    label: "Booked",
    variant: "default",
    className: "bg-primary text-primary-foreground",
    icon: UserCheck,
  },
};

interface AvailabilityStatusBadgeProps {
  status: AvailabilityStatus;
  showIcon?: boolean;
  className?: string;
}

export function AvailabilityStatusBadge({
  status,
  showIcon = true,
  className,
}: AvailabilityStatusBadgeProps) {
  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <Badge
      variant={config.variant}
      className={cn(config.className, "gap-1", className)}
    >
      {showIcon && <Icon className="h-3 w-3" />}
      {config.label}
    </Badge>
  );
}

export function getStatusColor(status: AvailabilityStatus): string {
  const colors: Record<AvailabilityStatus, string> = {
    unknown: "bg-muted",
    requested: "bg-amber-100 dark:bg-amber-950/30",
    not_available: "bg-destructive/10",
    available: "bg-emerald-100 dark:bg-emerald-950/30",
    booked: "bg-primary/10",
  };
  return colors[status];
}

export const availabilityStatuses: AvailabilityStatus[] = [
  "unknown",
  "requested",
  "not_available",
  "available",
  "booked",
];
